import type { ClipAudioOptions, ClipCameraOptions, ClipPipPosition } from './ClipsApi'

/** Ordered by preference. mp4 first (Safari/iOS WebView), webm fallback (Chrome/Android). */
export const PREFERRED_MIME_TYPES: readonly string[] = [
  'video/mp4;codecs=h264,aac',
  'video/mp4',
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
]

export function selectMimeType(override?: string): string {
  const MR = (globalThis as unknown as { MediaRecorder?: typeof MediaRecorder })
    .MediaRecorder
  if (!MR || typeof MR.isTypeSupported !== 'function') {
    throw new Error('[clips] MediaRecorder is not available in this environment')
  }
  if (override) {
    if (!MR.isTypeSupported(override)) {
      throw new Error(`[clips] mimeType "${override}" is not supported here`)
    }
    return override
  }
  for (const type of PREFERRED_MIME_TYPES) {
    if (MR.isTypeSupported(type)) return type
  }
  throw new Error('[clips] no supported MediaRecorder mime type found')
}

export interface NormalizedAudio {
  microphone: boolean
  gameAudio: boolean
}

/** Both knobs default ON ("game audio + mic"); gameAudio is only effective if a node is registered. */
export function normalizeAudio(audio: ClipAudioOptions | undefined): NormalizedAudio {
  return {
    microphone: audio?.microphone ?? true,
    gameAudio: audio?.gameAudio ?? true,
  }
}

export function resolveCanvas(
  target: HTMLCanvasElement | string | undefined,
): HTMLCanvasElement {
  if (target instanceof HTMLCanvasElement) return target
  const selector = target ?? 'canvas'
  const el = document.querySelector(selector)
  if (!(el instanceof HTMLCanvasElement)) {
    throw new Error(
      `[clips] no <canvas> found for "${selector}"; pass options.canvas explicitly`,
    )
  }
  return el
}

export function isRecordingSupported(): boolean {
  const g = globalThis as unknown as {
    MediaRecorder?: unknown
    HTMLCanvasElement?: { prototype?: { captureStream?: unknown } }
  }
  return (
    typeof g.MediaRecorder !== 'undefined' &&
    typeof g.HTMLCanvasElement?.prototype?.captureStream === 'function'
  )
}

export function isMicrophoneSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getUserMedia === 'function'
  )
}

export function isCameraSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getUserMedia === 'function' &&
    typeof HTMLVideoElement !== 'undefined'
  )
}

export type DevicePermissionState = 'granted' | 'prompt' | 'denied'

/**
 * Query whether the browser/device already has a given media permission.
 * Uses the Permissions API (Chrome/Firefox). Safari and most WebViews do not
 * support querying 'microphone'/'camera', so the fallback returns 'granted'
 * which triggers the platform consent dialog — a harmless double-prompt on
 * first use is preferable to silently skipping consent.
 *
 * TODO: Eliminate the Safari/iOS double-prompt by adding a host RPC that
 * queries native permission state (expo-permissions on RN, Permissions API
 * on web) and returning the result to the SDK.
 */
export async function queryDevicePermission(
  name: 'microphone' | 'camera',
): Promise<DevicePermissionState> {
  try {
    const perms = (navigator as Navigator & { permissions?: Permissions }).permissions
    if (!perms?.query) return 'granted'
    const result = await perms.query({ name: name as PermissionName })
    if (result.state === 'granted' || result.state === 'denied' || result.state === 'prompt') {
      return result.state
    }
    return 'granted'
  } catch {
    return 'granted'
  }
}

export interface ResolvedPip {
  position: ClipPipPosition
  widthFraction: number
  margin: number
  borderRadius: number
  mirror: boolean
}

export interface NormalizedCamera {
  enabled: boolean
  facingMode: 'user' | 'environment'
  pip: ResolvedPip
}

const DEFAULT_PIP: ResolvedPip = {
  position: 'top-center',
  widthFraction: 0.25,
  margin: 16,
  borderRadius: 12,
  mirror: true,
}

export function normalizeCamera(
  camera: boolean | ClipCameraOptions | undefined,
): NormalizedCamera {
  if (!camera) return { enabled: false, facingMode: 'user', pip: DEFAULT_PIP }
  const opts = camera === true ? {} : camera
  const facingMode = opts.facingMode ?? 'user'
  return {
    enabled: true,
    facingMode,
    pip: {
      ...DEFAULT_PIP,
      mirror: facingMode === 'user',
      position: opts.pip?.position ?? DEFAULT_PIP.position,
      widthFraction: opts.pip?.widthFraction ?? DEFAULT_PIP.widthFraction,
    },
  }
}
